import { useRef, useState } from 'react'
import { Box, Button, Chip, List, ListItem, ListItemText, Stack } from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { caricaDocumento, elencaDocumenti, TIPI_DOCUMENTO, documentiRichiesti, type TipoDocumento } from './documenti.api'
import type { TipologiaImpianto } from '@praticheenergia/domain'

const ETICHETTA_TIPO: Record<TipoDocumento, string> = {
  ultima_dichiarazione: 'Ultima dichiarazione',
  crediti_accise: 'Crediti accise',
  relazione_tecnica: 'Relazione tecnica',
}

interface Props {
  readonly impiantoId: number
  readonly tipologia: TipologiaImpianto
}

export function DocumentiImpianto({ impiantoId, tipologia }: Props) {
  const queryClient = useQueryClient()
  const [erroreCaricamento, setErroreCaricamento] = useState<string | null>(null)
  const [tipoInCorso, setTipoInCorso] = useState<TipoDocumento | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const {
    data: documenti = [],
    isLoading,
    error,
  } = useQuery({ queryKey: ['documenti', impiantoId], queryFn: () => elencaDocumenti(impiantoId) })

  const richiesti = documentiRichiesti(tipologia)

  function apriSelezione(tipo: TipoDocumento) {
    setTipoInCorso(tipo)
    inputRef.current?.click()
  }

  async function suFileScelto(file: File | undefined) {
    if (!file || !tipoInCorso) return
    setErroreCaricamento(null)
    try {
      await caricaDocumento(impiantoId, tipoInCorso, file)
      await queryClient.invalidateQueries({ queryKey: ['documenti', impiantoId] })
    } catch (e) {
      setErroreCaricamento(e instanceof Error ? e.message : 'errore imprevisto')
    } finally {
      setTipoInCorso(null)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <Box>
      {error && <p role="alert">{error instanceof Error ? error.message : 'Errore nel caricamento dei documenti'}</p>}
      {erroreCaricamento && <p role="alert">{erroreCaricamento}</p>}
      <input
        ref={inputRef}
        type="file"
        hidden
        onChange={(e) => void suFileScelto(e.target.files?.[0])}
      />
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        {TIPI_DOCUMENTO.map((tipo) => (
          <Button key={tipo} size="small" variant="outlined" onClick={() => apriSelezione(tipo)}>
            Carica {ETICHETTA_TIPO[tipo]}
            {richiesti.includes(tipo) && <Chip label="richiesto" size="small" color="warning" sx={{ ml: 1 }} />}
          </Button>
        ))}
      </Stack>
      <List>
        {!isLoading && documenti.length === 0 && <ListItemText primary="Nessun documento caricato" />}
        {documenti.map((d) => (
          <ListItem key={d.id} divider>
            <ListItemText primary={d.nomeFile} secondary={ETICHETTA_TIPO[d.tipo]} />
          </ListItem>
        ))}
      </List>
    </Box>
  )
}
